/**
 * DomainSelector - Component for selecting a domain for website creation
 */

import React, { FC, useState } from 'react';
import {
  Button,
  Flex,
  Text,
  VStack,
  Box,
  Input,
  InputGroup,
  InputRightAddon,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { useRealtimeChatContext } from '../hooks/useRealtimeChatContext';

interface DomainSelectorProps {
  domains?: string[];
}

/**
 * Get the domain suffix based on current environment
 * Staging/Development: .stage.website.fusionads.ai
 * Production: .website.fusionads.ai
 */
const getDomainSuffix = (): string => {
  const hostname = window.location.hostname.toLowerCase();
  // Use stage domain for staging environment or localhost
  if (hostname.includes('stage.website.fusionads.ai') || hostname.includes('stage.fusionads.ai') || hostname.includes('localhost')) {
    return '.stage.website.fusionads.ai';
  }
  return '.website.fusionads.ai';
};

/**
 * Extract subdomain from a full domain (handles both stage and non-stage formats)
 */
const extractSubdomain = (domain: string): string => {
  return domain
    .replace('.stage.website.fusionads.ai', '')
    .replace('.website.fusionads.ai', '');
};

const DomainSelector: FC<DomainSelectorProps> = ({ domains = [] }) => {
  const { sendMessage } = useRealtimeChatContext();
  const toast = useToast();
  const [customSubdomain, setCustomSubdomain] = useState('');
  const [isCheckingCustom, setIsCheckingCustom] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Get the domain suffix for current environment
  const domainSuffix = getDomainSuffix();

  // Parse domains from JSON string if needed
  const parsedDomains = typeof domains === 'string' ? JSON.parse(domains) : domains;
  
  // Normalize domains to use the correct environment suffix
  const normalizedDomains = parsedDomains.map((domain: string) => {
    const subdomain = extractSubdomain(domain);
    return `${subdomain}${domainSuffix}`;
  });
  
  // No domain pre-selected - user must choose
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);

  const handleSelectDomain = async (domain: string) => {
    setSelectedDomain(domain);
    setIsSelecting(true);
    // Send message to backend - the LLM will interpret this and call select_domain
    sendMessage(`I want to use the domain ${domain}`);
  };

  const handleCustomDomainSubmit = async () => {
    if (!customSubdomain.trim()) {
      toast({
        title: 'Please enter a subdomain',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    // Validate subdomain format
    const normalizedSubdomain = customSubdomain
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');

    if (normalizedSubdomain.length < 3) {
      toast({
        title: 'Subdomain too short',
        description: 'Please enter at least 3 characters',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsCheckingCustom(true);
    // Send the full domain with the correct environment suffix
    const fullDomain = `${normalizedSubdomain}${domainSuffix}`;
    sendMessage(`I want to use the custom domain ${fullDomain}`);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCustomDomainSubmit();
    }
  };

  return (
    <Box
      borderRadius="xl"
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      p={{ base: 4, sm: 6 }}
      maxW="550px"
      boxShadow="md"
    >
      <VStack spacing={5} align="stretch">
        {/* Header */}
        <VStack align="start" spacing={1}>
          <Text
            fontWeight="bold"
            fontSize="lg"
            color="gray.800"
            fontFamily="'Noto Sans', 'Open Sans', sans-serif"
          >
            Choose Your Domain
          </Text>
          <Text
            fontSize="sm"
            color="gray.500"
            fontFamily="'Noto Sans', 'Open Sans', sans-serif"
          >
            Select from our suggestions or enter your own
          </Text>
        </VStack>

        {/* Suggested domains */}
        {normalizedDomains.length > 0 && (
          <VStack spacing={3} align="stretch">
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wider"
            >
              Available Domains
            </Text>
            {normalizedDomains.map((domain: string, index: number) => {
              const isSelected = selectedDomain === domain;
              return (
                <Flex
                  key={domain}
                  align="center"
                  gap={3}
                >
                  {/* Number indicator */}
                  <Text
                    fontSize="xl"
                    fontWeight="bold"
                    color="gray.800"
                    minW="24px"
                    fontFamily="'Noto Sans', 'Open Sans', sans-serif"
                  >
                    {index + 1}
                  </Text>

                  {/* Domain option */}
                  <Flex
                    flex={1}
                    align="center"
                    justify="space-between"
                    p={{ base: 3, sm: 4 }}
                    borderRadius="lg"
                    border="2px solid"
                    borderColor={isSelected ? 'orange.400' : 'gray.200'}
                    bg={isSelected ? 'orange.50' : 'gray.50'}
                    cursor={isSelecting ? 'not-allowed' : 'pointer'}
                    opacity={isSelecting && !isSelected ? 0.5 : 1}
                    onClick={() => !isSelecting && handleSelectDomain(domain)}
                    _hover={
                      !isSelecting
                        ? {
                            borderColor: isSelected ? 'orange.400' : 'orange.200',
                            bg: isSelected ? 'orange.50' : 'orange.50',
                          }
                        : {}
                    }
                    transition="all 0.2s"
                    minW={0}
                  >
                    <Text
                      fontSize={{ base: 'sm', sm: 'md' }}
                      fontWeight="semibold"
                      color={isSelected ? 'orange.600' : 'gray.700'}
                      fontFamily="'Noto Sans', 'Open Sans', sans-serif"
                      wordBreak="break-all"
                    >
                      {domain}
                    </Text>
                    <Flex align="center" gap={2}>
                      {isSelected && isSelecting && <Spinner size="sm" color="orange.500" />}
                      {isSelected && !isSelecting && (
                        <Text color="orange.500" fontWeight="bold">
                          ✓
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                </Flex>
              );
            })}
          </VStack>
        )}

        {/* Divider */}
        <Flex align="center" gap={3}>
          <Box flex="1" h="1px" bg="gray.200" />
          <Text fontSize="sm" color="gray.400" fontFamily="'Noto Sans', 'Open Sans', sans-serif">
            or
          </Text>
          <Box flex="1" h="1px" bg="gray.200" />
        </Flex>

        {/* Custom domain input */}
        {!showCustomInput ? (
          <Button
            variant="ghost"
            color="orange.500"
            _hover={{ bg: 'orange.50' }}
            onClick={() => setShowCustomInput(true)}
            isDisabled={isSelecting}
            fontFamily="'Noto Sans', 'Open Sans', sans-serif"
          >
            Enter a custom subdomain
          </Button>
        ) : (
          <VStack spacing={3} align="stretch">
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wider"
            >
              Custom Domain
            </Text>
            <InputGroup size={{ base: 'md', sm: 'lg' }}>
              <Input
                placeholder="your-business"
                value={customSubdomain}
                onChange={(e) => setCustomSubdomain(e.target.value)}
                onKeyPress={handleKeyPress}
                borderRadius="lg"
                fontFamily="'Noto Sans', 'Open Sans', sans-serif"
                isDisabled={isCheckingCustom}
              />
              <InputRightAddon
                bg="gray.100"
                color="gray.500"
                fontSize="xs"
                fontFamily="'Noto Sans', 'Open Sans', sans-serif"
              >
                {domainSuffix}
              </InputRightAddon>
            </InputGroup>
            <Button
              bg="orange.base"
              color="white"
              _hover={{ bg: 'orange.600' }}
              size="md"
              onClick={handleCustomDomainSubmit}
              isLoading={isCheckingCustom}
              loadingText="Checking..."
              isDisabled={!customSubdomain.trim()}
              borderRadius="lg"
              fontFamily="'Noto Sans', 'Open Sans', sans-serif"
            >
              Check Availability
            </Button>
          </VStack>
        )}

        {/* Help text */}
        <Text
          fontSize="xs"
          color="gray.400"
          textAlign="center"
          fontFamily="'Noto Sans', 'Open Sans', sans-serif"
        >
          Your website will be available at this address
        </Text>
      </VStack>
    </Box>
  );
};

export default DomainSelector;
